import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatRupiahFull, formatTanggalID } from "@/lib/format";
import { Plus, Edit2, Trash2, PiggyBank } from "lucide-react";
import TabunganForm from "@/pages/forms/TabunganForm";
import SetorTabunganForm from "@/pages/forms/SetorTabunganForm";
import DeleteConfirm from "@/components/DeleteConfirm";
import { toast } from "sonner";

export default function Tabungan() {
  const [items, setItems] = useState([]);
  const [rekening, setRekening] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [setorOpen, setSetorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [t, r] = await Promise.all([
        api.get("/tabungan"),
        api.get("/rekening"),
      ]);
      setItems(t.data);
      setRekening(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const doDelete = async () => {
    try {
      await api.delete(`/tabungan/${confirmDel.id}`);
      toast.success("Goal dihapus");
      setConfirmDel(null);
      load();
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  return (
    <div className="pb-40" data-testid="tabungan-page">
      <div className="px-4 pt-5 pb-3 flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: "#DEEAF1", color: "#1F4E5F" }}
        >
          <PiggyBank size={20} />
        </div>
        <div>
          <div className="text-xs text-slate-500">Goal tabungan</div>
          <div className="text-base font-extrabold text-slate-900">
            {items.length} goal aktif
          </div>
        </div>
      </div>

      <div className="px-4 space-y-3">
        {loading ? (
          <div className="text-xs text-slate-500 py-8 text-center">
            Memuat...
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center text-xs text-slate-500 border border-slate-100">
            Belum ada goal tabungan.
          </div>
        ) : (
          items.map((t) => {
            const pct = Math.min(
              100,
              Math.round((t.terkumpul / t.target) * 100)
            );
            const done = t.terkumpul >= t.target;
            return (
              <div
                key={t.id}
                className="rounded-2xl p-4 border border-slate-100"
                style={{ backgroundColor: done ? "#E2EFDA" : "#DEEAF1" }}
                data-testid={`tabungan-row-${t.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-slate-900 truncate">
                      {t.nama}
                    </div>
                    <div className="text-[10px] text-slate-600 mt-0.5">
                      Target {formatRupiahFull(t.target)}
                      {t.deadline
                        ? ` · Deadline ${formatTanggalID(t.deadline)}`
                        : ""}
                    </div>
                  </div>
                  <div className="text-right ml-2">
                    <div className="text-[10px] text-slate-500">Terkumpul</div>
                    <div
                      className="text-sm font-extrabold"
                      style={{ color: done ? "#375623" : "#1F4E5F" }}
                    >
                      {formatRupiahFull(t.terkumpul)}
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="flex justify-between text-[10px] text-slate-600 mb-1">
                    <span>{pct}%</span>
                    <span>{formatRupiahFull(Math.max(0, t.target - t.terkumpul))} lagi</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/80 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: done ? "#375623" : "#2E75B6",
                      }}
                    />
                  </div>
                </div>
                <div className="flex gap-1.5 mt-3">
                  <button
                    onClick={() => {
                      setEditing(t);
                      setFormOpen(true);
                    }}
                    data-testid={`tabungan-edit-${t.id}`}
                    className="text-[10px] px-2 py-1 rounded-md bg-white text-slate-700 font-semibold tap flex items-center gap-1"
                  >
                    <Edit2 size={10} /> Edit
                  </button>
                  <button
                    onClick={() => setConfirmDel(t)}
                    data-testid={`tabungan-delete-${t.id}`}
                    className="text-[10px] px-2 py-1 rounded-md font-semibold tap flex items-center gap-1 text-white"
                    style={{ backgroundColor: "#C00000" }}
                  >
                    <Trash2 size={10} /> Hapus
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div
        className="fixed left-1/2 -translate-x-1/2 w-full max-w-[380px] px-4 grid grid-cols-2 gap-2"
        style={{ bottom: "80px" }}
      >
        <button
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          data-testid="tambah-tabungan-btn"
          className="h-12 rounded-xl bg-white border border-slate-200 text-[#1F4E5F] font-semibold text-sm tap shadow-lg flex items-center justify-center gap-1"
        >
          <Plus size={14} /> Tambah
        </button>
        <button
          onClick={() => setSetorOpen(true)}
          data-testid="setor-tabungan-btn"
          className="h-12 rounded-xl text-white font-semibold text-sm tap shadow-lg flex items-center justify-center gap-1"
          style={{ backgroundColor: "#1F4E5F" }}
        >
          <PiggyBank size={14} /> Setor
        </button>
      </div>

      <TabunganForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        existing={editing}
        onSaved={load}
      />
      <SetorTabunganForm
        open={setorOpen}
        onClose={() => setSetorOpen(false)}
        tabunganList={items}
        rekeningList={rekening}
        onSaved={load}
      />
      <DeleteConfirm
        open={!!confirmDel}
        onCancel={() => setConfirmDel(null)}
        onConfirm={doDelete}
        itemName={confirmDel?.nama}
        testId="delete-tabungan-dialog"
      />
    </div>
  );
}
